import { useState, useContext } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ExplainedButton as Button } from '../explained-button';
import { ThemeDto } from "../../general/dtos";
import { GeneralErrorDialog } from '../dialogs/error-dialog';
import { ThemesDataContext, ThemesDataContextValue } from '../../providers/theme-management-provider';
import { DeleteEntityConfirmDialog } from '../dialogs/delete-entity-confirm';
import '../../assets/entity-list.css';
import { CreateThemeDialog } from "./create-theme-dialog";
import { useContentPageNavigation } from '../content-page';
import { useUnsavedActions } from '../../general/unsaved-changes-context';

export function ThemeList() {
    const { requestAction } = useUnsavedActions();
    const { showDetails } = useContentPageNavigation();
    const ctx = useContext<ThemesDataContextValue | null>(ThemesDataContext);
    const { themes, loading, setSelectedTheme, selectedTheme, mode, setMode, deleteTheme } = ctx!;

    const [error, setError] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [showCreate, setShowCreate] = useState(false);

    const handleCreate = async (name: string, base: string) => requestAction(() => {
        const newTheme: ThemeDto = {
            label: name,
            value: base // assign it to base so that the original theme is downloaded by the context provide. SHOULD BE UPDATED BEFORE CALLING CREATE API
        };

        setSelectedTheme(newTheme);
        setMode!("create");
        showDetails();
        
        setShowCreate(false);
    });

    const handleEdit = async (row: ThemeDto) => requestAction(() => {
        setSelectedTheme(row);
        setMode!("edit");
        showDetails();
    });

    const handleDelete = async () => {
        setShowDeleteConfirm(false);
        const selectedThemeValue: string = selectedTheme?.value ?? '';
        if (selectedThemeValue.length > 0)
        {
            deleteTheme(selectedThemeValue);
        }
    };

    return (<>
        
        <div className="entity-list-container">
            <div className="entity-list-toolbar admin-toolbar">
                <Button label="New&nbsp;theme..." icon="pi pi-plus" className="p-button-text p-button-sm" 
                    onClick={() => setShowCreate(true)} disabled={mode !== 'view'} disabledReason="Save or cancel the current theme changes before creating another theme."/>
            </div>

            <div className="entity-list-wrapper">
                <DataTable
                    value={themes.custom || []}
                    loading={loading}
                    selectionMode="single"
                    selection={selectedTheme}
                    onSelectionChange={(e) => {
                        const value = (e.value ?? selectedTheme) as ThemeDto | null;
                        if (value !== null) {
                            if (value !== selectedTheme) requestAction(() => { setMode('view'); setSelectedTheme(value); });
                            showDetails();
                        }
                    }}
                    dataKey="value"
                    scrollable
                    scrollHeight="flex"
                    emptyMessage="No custom themes found."
                >
                    <Column field="label" header="Theme name" sortable bodyStyle={{ whiteSpace: 'nowrap' }}/>
                    <Column header="Actions" bodyStyle={{ whiteSpace: 'nowrap' }} body={(row: ThemeDto) => (
                        <span className="admin-row-actions">
                            <Button icon="pi pi-pencil" aria-label="Edit theme" tooltip="Edit theme" className="p-button-text" onClick={() => handleEdit(row)} />
                            <Button icon="pi pi-trash" aria-label="Delete theme" tooltip="Delete theme" className="p-button-text p-button-danger" onClick={() => requestAction(() => {
                                setSelectedTheme(row);
                                setShowDeleteConfirm(true);
                            })}/>
                        </span>
                    )} />
                </DataTable>
            </div>
        </div>

        <CreateThemeDialog 
            visible={showCreate} 
            onCancel={() => setShowCreate(false)} 
            onCreate={handleCreate} 
        />

        <DeleteEntityConfirmDialog
            visible={showDeleteConfirm}
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            entityType='Theme'
            entityName={selectedTheme?.label ?? ''}
        />

        <GeneralErrorDialog
            visible={error.length > 0}
            title="Error"
            message={error}
            onClose={() => setError('')}
        />
    </>);
}
